import React, { useEffect, useRef, useState } from 'react';
import { supportedCurrencies } from '../../constants/supportedCurrencies';

function CurrencyPicker({ value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  const filtered = search.trim()
    ? supportedCurrencies.filter(
        (c) =>
          c.code.toLowerCase().includes(search.toLowerCase()) ||
          c.label.toLowerCase().includes(search.toLowerCase())
      )
    : supportedCurrencies;

  const selectedLabel = supportedCurrencies.find((c) => c.code === value);

  return (
    <div ref={wrapperRef} style={wrapperStyle}>
      <button
        type="button"
        onClick={() => {
          if (!disabled) {
            setOpen((prev) => !prev);
            setSearch('');
          }
        }}
        style={{
          ...triggerStyle,
          opacity: disabled ? 0.7 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
        disabled={disabled}
      >
        <div style={triggerInnerStyle}>
          <span style={triggerCodeStyle}>{selectedLabel ? selectedLabel.label : value}</span>
        </div>
        <span style={chevronStyle}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={popupStyle}>
          <div style={searchRowStyle}>
            <input
              ref={searchRef}
              type="text"
              placeholder="Search currencies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={searchInputStyle}
            />
          </div>

          <div style={gridStyle}>
            {filtered.map((currency) => {
              const isSelected = currency.code === value;
              return (
                <button
                  key={currency.code}
                  type="button"
                  onClick={() => {
                    onChange(currency.code);
                    setOpen(false);
                    setSearch('');
                  }}
                  title={currency.label}
                  style={{
                    ...cellStyle,
                    ...(isSelected ? selectedCellStyle : null),
                  }}
                >
                  <span style={cellCodeStyle}>{currency.code}</span>
                  <span style={cellNameStyle}>
                    {currency.label.split(' — ')[1]}
                  </span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div style={emptyStyle}>No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const wrapperStyle = {
  position: 'relative',
  width: '100%',
};

const triggerStyle = {
  width: '100%',
  minHeight: '48px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--input-border)',
  background: 'var(--input-bg)',
  color: 'var(--text-primary)',
  padding: '12px 14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  textAlign: 'left',
  fontSize: '16px',
};

const triggerInnerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const triggerCodeStyle = {
  fontWeight: 600,
  fontSize: '16px',
  color: 'var(--text-primary)',
};


const chevronStyle = {
  fontSize: '10px',
  color: 'var(--text-secondary)',
  flexShrink: 0,
};

const popupStyle = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  borderRadius: '16px',
  boxShadow: 'var(--shadow)',
  zIndex: 100,
  padding: '12px',
  boxSizing: 'border-box',
};

const searchRowStyle = {
  marginBottom: '10px',
};

const searchInputStyle = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: '10px',
  border: '1px solid var(--border)',
  background: 'var(--input-bg)',
  color: 'var(--text-primary)',
  fontSize: '13px',
  boxSizing: 'border-box',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '6px',
  maxHeight: '260px',
  overflowY: 'auto',
};

const cellStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 4px',
  borderRadius: '10px',
  border: '1px solid var(--border)',
  background: 'var(--input-bg)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  gap: '2px',
};

const selectedCellStyle = {
  background: 'var(--primary)',
  border: '1px solid var(--primary)',
  color: '#fff',
};

const cellCodeStyle = {
  fontWeight: 800,
  fontSize: '14px',
};

const cellNameStyle = {
  fontSize: '10px',
  opacity: 0.75,
  textAlign: 'center',
  lineHeight: 1.2,
};

const emptyStyle = {
  gridColumn: '1 / -1',
  textAlign: 'center',
  padding: '16px',
  color: 'var(--text-secondary)',
  fontSize: '13px',
};

export default CurrencyPicker;
